import { createContext, ReactNode, useContext, useReducer } from 'react';
import produce from 'immer';
import {
  NamedEntityForEpisodeItem,
  LinkedNamedEntityReference,
} from '@insights_module/types/ContextualAndEntity';
import { CategoryType, GarmRiskLevelType } from '@insights_module/types/BrandSafety';
import { unimplemented } from '@utils/commonUtils';
import { ISegment, ParagraphData } from '@insights_module/types/Episode';
import { GenAIFromAudioContentType } from '@insights_module/hooks/useEpisodeDetailsData';

type CurrentAudioType = {
  paragraph: ParagraphData | undefined | null;
  segment: ISegment | undefined | null;
};

type EpisodeDetailsContextKeys =
  | 'insights.episode_details.selected_segment'
  | 'insights.episode_details.active_tab'
  | 'insights.episode_details.selected_garm_category'
  | 'insights.episode_details.selected_garm_level'
  | 'insights.episode_details.selected_contextual_category'
  | 'insights.episode_details.selected_entity'
  | 'insights.episode_details.search_term'
  | 'insights.episode_details.audio_current'
  | 'insights.episode_details.active_audio'
  | 'insights.episode_details.active_view'
  | 'insights.episode_details.selected_social_post_type'
  | 'insights.episode_details.selected_promotion_item'
  | 'insights.episode_details.selected_chapter_time'
  | 'insights.episode_details.show_transcript_timestamps'
  | 'insights.episode_details.show_speaker_labels'
  | 'insights.episode_details.has_diarized_transcript'
  | 'insights.episode_details.should_fetch_ai_content';

type EpisodeDetailsTabs = 'episode_overview' | 'segment_overview';

export type EpisodeDetailsActiveView =
  | 'garm_brand_safety'
  | 'iab_categories'
  | 'entities'
  | 'speakers'
  | 'chapters'
  | 'topics'
  | 'keywords'
  | 'social_posts'
  | 'show_notes'
  | 'summary'
  | 'article';

export type SocialPost = 'twitter' | 'facebook' | 'linkedin' | 'instagram';

const EPISODE_DETAILS_CONTEXT_KEYS = {
  SELECTED_SEGMENT: 'insights.episode_details.selected_segment' as EpisodeDetailsContextKeys,
  // TODO:Remove activeTab from context as we no longer need it
  ACTIVE_TAB: 'insights.episode_details.active_tab' as EpisodeDetailsContextKeys,
  SELECTED_GARM_CATEGORY:
    'insights.episode_details.selected_garm_category' as EpisodeDetailsContextKeys,
  SELECTED_GARM_LEVEL: 'insights.episode_details.selected_garm_level' as EpisodeDetailsContextKeys,
  SELECTED_CONTEXTUAL_CATEGORY:
    'insights.episode_details.selected_contextual_category' as EpisodeDetailsContextKeys,
  SELECTED_ENTITY: 'insights.episode_details.selected_entity' as EpisodeDetailsContextKeys,
  SEARCH_TERM: 'insights.episode_details.search_term' as EpisodeDetailsContextKeys,
  AUDIO_CURRENT: 'insights.episode_details.audio_current' as EpisodeDetailsContextKeys,
  ACTIVE_AUDIO: 'insights.episode_details.active_audio' as EpisodeDetailsContextKeys,
  ACTIVE_VIEW: 'insights.episode_details.active_view' as EpisodeDetailsContextKeys,
  SELECTED_SOCIAL_POST_TYPE:
    'insights.episode_details.selected_social_post_type' as EpisodeDetailsContextKeys,
  SELECTED_PROMOTION_ITEM:
    'insights.episode_details.selected_promotion_item' as EpisodeDetailsContextKeys,
  SELECTED_PROMOTION_ITEM_IS_GENERATING:
    'insights.episode_details.selected_promotion_item_is_generating' as EpisodeDetailsContextKeys,
  SELECTED_CHAPTER_TIME:
    'insights.episode_details.selected_chapter_time' as EpisodeDetailsContextKeys,
  SHOW_TRANSCRIPT_TIMESTAMPS:
    'insights.episode_details.show_transcript_timestamps' as EpisodeDetailsContextKeys,
  SHOW_SPEAKERS_LABEL: 'insights.episode_details.show_speakers_label' as EpisodeDetailsContextKeys,
  HAS_DIARIZED_TRANSCRIPT:
    'insights.episode_details.has_diarized_transcript' as EpisodeDetailsContextKeys,
};

export const EPISODE_DETAILS_TABS = {
  EPISODE_OVERVIEW: 'episode_overview' as EpisodeDetailsTabs,
  SEGMENT_OVERVIEW: 'segment_overview' as EpisodeDetailsTabs,
};

export const INSIGHTS_VIEW_ITEM = {
  GARM: 'garm_brand_safety',
  IAB: 'iab_categories',
  ENTITIES: 'entities',
  SPEAKERS: 'speakers',
} as const;

export const DETAILS_VIEW_ITEM = {
  CHAPTERS: 'chapters',
  TOPICS: 'topics',
  KEYWORDS: 'keywords',
} as const;

export const PROMOTION_VIEW_ITEM = {
  SOCIAL_POSTS: 'social_posts',
  SHOW_NOTES: 'show_notes',
  SUMMARY: 'summary',
  ARTICLE: 'article',
} as const;

export const SOCIAL_POST_ITEM = {
  TWITTER: 'twitter',
  FACEBOOK: 'facebook',
  LINKEDIN: 'linkedin',
  INSTAGRAM: 'instagram',
} as const;

export const INSIGHTS_VIEW_ITEMS: EpisodeDetailsActiveView[] = Object.values(
  INSIGHTS_VIEW_ITEM
) as EpisodeDetailsActiveView[];
export const DETAILS_VIEW_ITEMS: EpisodeDetailsActiveView[] = Object.values(
  DETAILS_VIEW_ITEM
) as EpisodeDetailsActiveView[];
export const PROMOTION_VIEW_ITEMS: EpisodeDetailsActiveView[] = Object.values(
  PROMOTION_VIEW_ITEM
) as EpisodeDetailsActiveView[];
export const SOCIAL_POST_ITEMS: SocialPost[] = Object.values(SOCIAL_POST_ITEM) as SocialPost[];

type Action = {
  type: EpisodeDetailsContextKeys;
  payload:
    | number
    | string
    | boolean
    | null
    | NamedEntityForEpisodeItem
    | LinkedNamedEntityReference
    | CurrentAudioType
    | EpisodeDetailsActiveView
    | SocialPost
    | GenAIFromAudioContentType;
};

export interface IEpisodeDetailsContext {
  selectedSegment: number | null;
  activeTab: EpisodeDetailsTabs;
  selectedGarmCategory: string | null;
  selectedGarmLevel: string | null;
  selectedContextualCategory: string | null;
  selectedEntity: NamedEntityForEpisodeItem | LinkedNamedEntityReference | null;
  searchTerm: string;
  currentAudio: {
    paragraph: ParagraphData | null;
    segment: ISegment | null;
  };
  isAudioPlaying: boolean;
  activeView: EpisodeDetailsActiveView;
  selectedSocialPostType: SocialPost;
  selectedPromotionItem: GenAIFromAudioContentType | null;
  selectedPromotionItemIsGenerating: boolean;
  selectedChapterTime: number | null;
  showTranscriptTimestamps: boolean;
  showSpeakerLabel: boolean;
  hasDiarizedTranscript: boolean;
  setSelectedSegment: (value: number | null) => void;
  setActiveTab: (value: EpisodeDetailsTabs) => void;
  setSelectedGarmCategory: (value: CategoryType | null) => void;
  setSelectedGarmLevel: (value: GarmRiskLevelType | null) => void;
  setSelectedContextualCategory: (value: string | null) => void;
  setSelectedEntity: (value: NamedEntityForEpisodeItem | LinkedNamedEntityReference | null) => void;
  setSearchTerm: (value: string | null) => void;
  setCurrentAudio: (value: CurrentAudioType) => void;
  setIsAudioPlaying: (value: boolean) => void;
  setActiveView: (value: EpisodeDetailsActiveView) => void;
  setSelectedSocialPostType: (value: SocialPost) => void;
  setSelectedPromotionItem: (value: GenAIFromAudioContentType | null) => void;
  setSelectedPromotionItemIsGenerating: (value: boolean) => void;
  setSelectedChapterTime: (value: number | null) => void;
  setShowTranscriptTimestamps: (value: boolean) => void;
  setShowSpeakerLabel: (value: boolean) => void;
  setHasDiarizedTranscript: (value: boolean) => void;
}

const initialState: IEpisodeDetailsContext = {
  selectedSegment: null,
  activeTab: EPISODE_DETAILS_TABS.EPISODE_OVERVIEW,
  selectedGarmCategory: null,
  selectedGarmLevel: null,
  selectedContextualCategory: null,
  selectedEntity: null,
  searchTerm: '',
  currentAudio: {
    paragraph: null,
    segment: null,
  },
  isAudioPlaying: false,
  activeView: INSIGHTS_VIEW_ITEM.GARM,
  selectedSocialPostType: SOCIAL_POST_ITEM.TWITTER,
  selectedPromotionItem: null,
  selectedPromotionItemIsGenerating: false,
  selectedChapterTime: null,
  showTranscriptTimestamps: true,
  showSpeakerLabel: true,
  hasDiarizedTranscript: false,
  setSelectedSegment: unimplemented,
  setActiveTab: unimplemented,
  setSelectedGarmCategory: unimplemented,
  setSelectedGarmLevel: unimplemented,
  setSelectedContextualCategory: unimplemented,
  setSelectedEntity: unimplemented,
  setSearchTerm: unimplemented,
  setCurrentAudio: unimplemented,
  setIsAudioPlaying: unimplemented,
  setActiveView: unimplemented,
  setSelectedSocialPostType: unimplemented,
  setSelectedPromotionItem: unimplemented,
  setSelectedPromotionItemIsGenerating: unimplemented,
  setSelectedChapterTime: unimplemented,
  setShowTranscriptTimestamps: unimplemented,
  setShowSpeakerLabel: unimplemented,
  setHasDiarizedTranscript: unimplemented,
};

const reducer = produce((draft: IEpisodeDetailsContext, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_SEGMENT:
      draft.selectedSegment = payload as number | null;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.ACTIVE_TAB:
      draft.activeTab = payload as EpisodeDetailsTabs;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_GARM_CATEGORY:
      draft.selectedGarmCategory = payload as CategoryType | null;
      draft.selectedGarmLevel = null;
      if (payload !== null) {
        draft.selectedContextualCategory = null;
        draft.selectedEntity = null;
        draft.searchTerm = '';
      }
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_GARM_LEVEL:
      draft.selectedGarmLevel = payload as GarmRiskLevelType | null;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_CONTEXTUAL_CATEGORY:
      draft.selectedContextualCategory = payload as string | null;
      if (payload !== null) {
        draft.selectedEntity = null;
        draft.selectedGarmCategory = null;
        draft.selectedGarmLevel = null;
        draft.searchTerm = '';
      }
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_ENTITY:
      draft.selectedEntity = payload as
        | NamedEntityForEpisodeItem
        | LinkedNamedEntityReference
        | null;
      if (payload !== null) {
        draft.selectedGarmCategory = null;
        draft.selectedGarmLevel = null;
        draft.selectedContextualCategory = null;
        draft.searchTerm = '';
      }
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SEARCH_TERM:
      draft.searchTerm = payload as string;
      draft.selectedGarmCategory = null;
      draft.selectedGarmLevel = null;
      draft.selectedContextualCategory = null;
      draft.selectedEntity = null;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.AUDIO_CURRENT: {
      const { paragraph, segment } = payload as {
        paragraph: ParagraphData | null;
        segment: ISegment | null;
      };

      if (segment) {
        draft.currentAudio.segment = segment as ISegment;
      }

      if (paragraph) {
        draft.currentAudio.paragraph = paragraph as ParagraphData;
      }

      break;
    }

    case EPISODE_DETAILS_CONTEXT_KEYS.ACTIVE_AUDIO:
      draft.isAudioPlaying = payload as boolean;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.ACTIVE_VIEW:
      draft.activeView = payload as EpisodeDetailsActiveView;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_SOCIAL_POST_TYPE:
      draft.selectedSocialPostType = payload as SocialPost;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_PROMOTION_ITEM:
      draft.selectedPromotionItem = payload as GenAIFromAudioContentType | null;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_PROMOTION_ITEM_IS_GENERATING:
      draft.selectedPromotionItemIsGenerating = payload as boolean;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_CHAPTER_TIME:
      draft.selectedChapterTime = payload as number | null;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SHOW_TRANSCRIPT_TIMESTAMPS:
      draft.showTranscriptTimestamps = payload as boolean;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.SHOW_SPEAKERS_LABEL:
      draft.showSpeakerLabel = payload as boolean;
      break;

    case EPISODE_DETAILS_CONTEXT_KEYS.HAS_DIARIZED_TRANSCRIPT:
      draft.hasDiarizedTranscript = payload as boolean;
      break;

    default:
      break;
  }
}, initialState);

export const EpisodeDetailsContext = createContext<IEpisodeDetailsContext>(initialState);

export const EpisodeDetailsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  function setSelectedSegment(value: number | null) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_SEGMENT, payload: value });
  }

  function setActiveTab(value: EpisodeDetailsTabs) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.ACTIVE_TAB, payload: value });
  }

  function setSelectedGarmCategory(value: CategoryType | null) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_GARM_CATEGORY, payload: value });
  }

  function setSelectedGarmLevel(value: GarmRiskLevelType | null) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_GARM_LEVEL, payload: value });
  }

  function setSelectedContextualCategory(value: string | null) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_CONTEXTUAL_CATEGORY, payload: value });
  }

  function setSelectedEntity(value: NamedEntityForEpisodeItem | LinkedNamedEntityReference | null) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_ENTITY, payload: value });
  }

  function setSearchTerm(value: string | null) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SEARCH_TERM, payload: value });
  }

  function setCurrentAudio(payload: CurrentAudioType) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.AUDIO_CURRENT, payload });
  }

  function setIsAudioPlaying(payload: boolean) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.ACTIVE_AUDIO, payload });
  }

  function setActiveView(payload: EpisodeDetailsActiveView) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.ACTIVE_VIEW, payload });
  }

  function setSelectedSocialPostType(payload: SocialPost) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_SOCIAL_POST_TYPE, payload });
  }

  function setSelectedPromotionItemIsGenerating(payload: boolean) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_PROMOTION_ITEM_IS_GENERATING, payload });
  }

  function setSelectedChapterTime(payload: number | null) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SELECTED_CHAPTER_TIME, payload });
  }

  function setShowTranscriptTimestamps(payload: boolean) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SHOW_TRANSCRIPT_TIMESTAMPS, payload });
  }

  function setShowSpeakerLabel(payload: boolean) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.SHOW_SPEAKERS_LABEL, payload });
  }

  function setHasDiarizedTranscript(payload: boolean) {
    dispatch({ type: EPISODE_DETAILS_CONTEXT_KEYS.HAS_DIARIZED_TRANSCRIPT, payload });
  }

  const value = {
    ...state,
    setSelectedSegment,
    setActiveTab,
    setSelectedGarmCategory,
    setSelectedGarmLevel,
    setSelectedContextualCategory,
    setSelectedEntity,
    setSearchTerm,
    setCurrentAudio,
    setIsAudioPlaying,
    setActiveView,
    setSelectedSocialPostType,
    setSelectedChapterTime,
    setShowTranscriptTimestamps,
    setShowSpeakerLabel,
    setHasDiarizedTranscript,
    setSelectedPromotionItemIsGenerating,
  };

  return <EpisodeDetailsContext.Provider value={value}>{children}</EpisodeDetailsContext.Provider>;
};

export const useEpisodeDetailsContext = () => {
  return useContext(EpisodeDetailsContext);
};
